import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// API functions for admin bookings
export const adminBookingApi = {
  // Get all bookings for admin view
  getAdminBookings: async (params = {}) => {
    const response = await api.get('/admin/bookings', { params });
    return response.data;
  },

  // Get a single booking for admin view
  getAdminBookingById: async (bookingId) => {
    const response = await api.get(`/admin/bookings/${bookingId}`);
    return response.data;
  },

  // Update booking status (admin can update any booking)
  updateBookingStatus: async ({ bookingId, status, status_notes }) => {
    const response = await api.put(`/admin/bookings/${bookingId}/status`, { status, status_notes });
    return response.data;
  },

  // Add notes to a booking (admin can add notes to any booking)
  addBookingNotes: async ({ bookingId, notes, note_type }) => {
    const response = await api.post(`/admin/bookings/${bookingId}/notes`, { notes, note_type });
    return response.data;
  },

  // Get booking statistics for admin
  getAdminBookingStatistics: async () => {
    const response = await api.get('/admin/bookings/statistics');
    return response.data;
  },

  // Export bookings data
  exportBookings: async (params = {}) => {
    const response = await api.get('/admin/bookings/export', { 
      params,
      responseType: 'blob' // For file downloads
    });
    return response.data;
  },
};

// React Query Hooks

// Get all admin bookings
export const useAdminBookings = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['adminBookings', params],
    queryFn: () => adminBookingApi.getAdminBookings(params),
    staleTime: 5 * 60 * 1000, // Data considered fresh for 5 minutes
    ...options
  });
};

// Get single admin booking details
export const useAdminBooking = (bookingId, options = {}) => {
  return useQuery({
    queryKey: ['adminBooking', bookingId],
    queryFn: () => adminBookingApi.getAdminBookingById(bookingId),
    enabled: !!bookingId, // Only run if bookingId is available
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

// Update admin booking status mutation
export const useUpdateAdminBookingStatus = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adminBookingApi.updateBookingStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['adminBookings']); // Invalidate all admin bookings
      queryClient.invalidateQueries(['adminBookingStatistics']); // Refresh tab counts
      queryClient.setQueryData(['adminBooking', data.data.id], data); // Update specific booking
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    },
    ...options
  });
};

// Add notes to admin booking mutation
export const useAddAdminBookingNotes = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: adminBookingApi.addBookingNotes,
    onSuccess: (data) => {
      queryClient.invalidateQueries(['adminBooking', data.data.booking_id]); // Invalidate specific booking to refetch notes
      options.onSuccess?.(data);
    },
    onError: (error) => {
      options.onError?.(error);
    },
    ...options
  });
};

// Get admin booking statistics
export const useAdminBookingStatistics = (options = {}) => {
  return useQuery({
    queryKey: ['adminBookingStatistics'],
    queryFn: adminBookingApi.getAdminBookingStatistics,
    staleTime: 15 * 60 * 1000, // Statistics can be less frequently updated
    ...options
  });
};

// Export bookings mutation
export const useExportAdminBookings = (options = {}) => {
  return useMutation({
    mutationFn: adminBookingApi.exportBookings,
    onSuccess: (data, variables) => {
      // Create download link for the exported file
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bookings-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      options.onSuccess?.(data, variables);
    },
    onError: (error) => {
      options.onError?.(error);
    },
    ...options
  });
};
